import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { AvatarWithInfo } from '@src/components/AvatarWithInfo';
import { TextWithReadMore } from '@src/containers/TextWithReadMore';
import { ActorNameWithStatus } from '@src/components/ActorNameWithStatus';
import { getNameInitials } from '@src/util/getNameInitials';
import { relativeTime } from '@src/util/relativeTime';
import { UserCompanyAssociationTypeEnum, useReplyToReviewMutation } from '@src/graphql/generated';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { AvatarSize } from '@src/components/Avatar/enums/avatarSize.enum';
import { trackEvent } from '@src/metrics';
import { CreateReply } from '../CreateReply';
import { RepliedComment } from '../RepliedComment';
import './styles.scss';

export const CompanyAwardFeedItem = ({ review, wrapperClassName }) => {
  const [createReply, { loading: isLoading }] = useReplyToReviewMutation();

  const {
    id: reviewId,
    actor: { entity: actorEntity },
    createdAt,
    comment,
    userAssociation,
    reply,
  } = review;

  const { name, position, avatar, avatarColor } = actorEntity;

  const onPost = (message: string): void => {
    createReply({
      variables: {
        replyToReviewInput: {
          comment: message,
        },
        reviewId: reviewId,
      },
    });

    trackEvent({
      context: MetricContext.COMPANY_REVIEWS_VIEW,
      interaction: MetricAction.REPLY_REVIEW,
      payload: {
        'Review Id': reviewId,
      },
    });
  };

  return (
    <div className={classnames('award-feed-item', wrapperClassName)}>
      <div className="award-feed-item__header">
        <AvatarWithInfo
          avatarProps={{
            avatarColor: avatarColor,
            initials: getNameInitials(name),
            size: AvatarSize.X_LARGE,
            source: avatar?.fullUrl,
          }}
          name={
            <ActorNameWithStatus
              fullName={name}
              isWorking={userAssociation === UserCompanyAssociationTypeEnum.WORKING}
            />
          }
          subTitle={`${position} • ${relativeTime(createdAt)}`}
          wrapperClassName="award-feed-item__user-info"
        />
      </div>
      <div className="award-feed-item__body">
        <TextWithReadMore forceInline lineLimit={2} text={comment} textClassName="award-feed-item__comment" />
      </div>
      <div className="raward-feed-item__footer">
        {reply ? (
          <RepliedComment reply={reply} />
        ) : (
          <CreateReply isLoading={isLoading} onPost={message => onPost(message)} />
        )}
      </div>
    </div>
  );
};

CompanyAwardFeedItem.propTypes = {
  onPost: PropTypes.func.isRequired,
  review: PropTypes.shape({
    actor: PropTypes.shape({
      entity: PropTypes.object.isRequired,
    }).isRequired,
    comment: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    userAssociation: PropTypes.string.isRequired,
  }).isRequired,
  wrapperClassName: PropTypes.string,
};
